import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FiFileText, FiUpload, FiCheck, FiLoader } from 'react-icons/fi';
import Button from '../../../shared/components/ui/Button';
import {
  draftCompanyFromDocument,
  draftCompanyFromDescription
} from '../../services/api/implementation/impl';
import { DOCUMENT_CATALOGUE } from '../../constants/kyb';

/**
 * Smart Fill — AI draft extraction for company onboarding.
 *
 * ON THE MOCKUP'S PROGRESS LIST: the design shows a five-step stream (Reading Company
 * Name → Understanding Business Category → Matching GST → Finding Address → Predicting
 * Company Type) animating to 60%. Extraction is a single synchronous request, and two of
 * those steps ("Matching GST", "Finding Address") describe registry lookups that do not
 * exist — there is no verification API. So the stepper below reports only transitions
 * that genuinely occur: sending the source, extraction returning, fields applied. A real
 * streamed step list would need a progress endpoint the backend does not have.
 *
 * ON "Confidence: 95%": the mockup shows a confidence chip before Generate is pressed.
 * Confidence does not exist until extraction has run, so it is shown afterwards, per
 * field, from the model's own reported value.
 *
 * The Website source from the mockup is not offered — the backend has no website import
 * endpoint (it is an SSRF surface that needs its own hardening).
 */

const SOURCE_DOC_TYPES = [
  'gst_certificate',
  'incorporation_certificate',
  'partnership_deed',
  'pan_card'
];

const STEPS = [
  { key: 'send', label: 'Reading your source' },
  { key: 'extract', label: 'Extracting company fields' },
  { key: 'apply', label: 'Filling the form' }
];

const SmartFillPanel = ({ onApplyDraft, disabled }) => {
  const [description, setDescription] = useState('');
  const [stage, setStage] = useState(null); // null | 'send' | 'extract' | 'apply' | 'done'
  const [lastResult, setLastResult] = useState(null);

  const isBusy = stage !== null && stage !== 'done';

  const sourceOptions = DOCUMENT_CATALOGUE.filter((doc) =>
    SOURCE_DOC_TYPES.includes(doc.value)
  );

  const handleResult = useCallback(
    (result) => {
      setStage('apply');
      const fields = result?.draft?.fields || {};
      const count = Object.keys(fields).length;

      if (result?.mode === 'disabled') {
        toast('AI assist is switched off — please enter the details manually.', {
          icon: 'ℹ️'
        });
      } else if (result?.mode === 'failed') {
        // Show why. Reporting a credits/timeout failure as "switched off" sends people
        // to check env vars that are already correct.
        toast.error(result.error || 'Extraction failed — please enter the details manually.');
      } else if (count === 0) {
        toast('Nothing could be read from that source. Please enter the details manually.');
      } else {
        toast.success(`${count} field${count === 1 ? '' : 's'} filled — please check each one.`);
        if (onApplyDraft) onApplyDraft(result.draft);
      }

      setLastResult(result);
      setStage('done');
    },
    [onApplyDraft]
  );

  const handleFailure = useCallback((err) => {
    toast.error(
      err?.response?.data?.message ||
        'Could not read that source. Please enter the details manually.'
    );
    setStage(null);
  }, []);

  const runDocument = useCallback(
    async (docType, file) => {
      if (!file) return;
      setStage('send');
      try {
        setStage('extract');
        handleResult(await draftCompanyFromDocument(file, docType));
      } catch (err) {
        handleFailure(err);
      }
    },
    [handleResult, handleFailure]
  );

  const runDescription = useCallback(async () => {
    if (!description.trim()) {
      toast.error('Describe the company first.');
      return;
    }
    setStage('send');
    try {
      setStage('extract');
      handleResult(await draftCompanyFromDescription(description.trim()));
    } catch (err) {
      handleFailure(err);
    }
  }, [description, handleResult, handleFailure]);

  const stepState = (key) => {
    if (stage === 'done') return 'done';
    const order = STEPS.map((step) => step.key);
    const current = order.indexOf(stage);
    const mine = order.indexOf(key);
    if (current < 0) return 'idle';
    if (mine < current) return 'done';
    if (mine === current) return 'active';
    return 'idle';
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-stone-800">✨ Smart Fill</span>
        <span className="text-xs text-stone-500">Optional — you can type everything yourself</span>
      </div>

      <textarea
        className="mt-3 w-full rounded border border-stone-300 p-2 text-sm"
        rows={3}
        disabled={disabled || isBusy}
        placeholder="Describe your company. Example: We're a residential developer in Bangalore focusing on premium high-rises and integrated townships…"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <p className="mt-1 text-xs text-stone-500">
        A description can suggest the trade name and entity type only. PAN, GSTIN, CIN and
        the registered address are taken from documents, never from prose.
      </p>

      <div className="mt-3 flex justify-end">
        <Button onClick={runDescription} disabled={disabled || isBusy || !description.trim()}>
          {isBusy ? 'Working…' : 'Generate'}
        </Button>
      </div>

      <div className="my-3 flex items-center gap-3">
        <span className="h-px flex-1 bg-stone-200" />
        <span className="text-xs uppercase tracking-wide text-stone-400">
          or extract from a document
        </span>
        <span className="h-px flex-1 bg-stone-200" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {sourceOptions.map((doc) => (
          <label
            key={doc.value}
            htmlFor={`smartfill-${doc.value}`}
            className={`relative flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-xs ${
              disabled || isBusy
                ? 'border-stone-200 bg-stone-100 text-stone-400'
                : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
            }`}>
            <input
              id={`smartfill-${doc.value}`}
              type="file"
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              accept=".pdf,.png,.jpg,.jpeg"
              disabled={disabled || isBusy}
              onChange={(event) => runDocument(doc.value, event.target.files?.[0])}
            />
            {doc.value === 'gst_certificate' ? <FiFileText /> : <FiUpload />}
            {doc.label}
          </label>
        ))}
      </div>

      {/*
        Hidden once we know AI is unavailable: a list of green ticks reads as "extraction
        ran and succeeded", which is the opposite of what happened.
      */}
      {stage !== null && lastResult?.mode !== 'disabled' && lastResult?.mode !== 'failed' && (
        <ul className="mt-4 flex flex-col gap-1.5">
          {STEPS.map((step) => {
            const state = stepState(step.key);
            return (
              <li key={step.key} className="flex items-center gap-2 text-xs">
                {state === 'done' ? (
                  <FiCheck className="text-emerald-600" />
                ) : state === 'active' ? (
                  <FiLoader className="animate-spin text-emerald-600" />
                ) : (
                  <span className="h-3 w-3 rounded-full border border-stone-300" />
                )}
                <span className={state === 'idle' ? 'text-stone-400' : 'text-stone-700'}>
                  {step.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {lastResult?.draft?.warnings?.length > 0 && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2">
          {lastResult.draft.warnings.map((warning) => (
            <p key={warning} className="text-xs text-amber-800">
              {warning}
            </p>
          ))}
        </div>
      )}

      {lastResult?.mode === 'disabled' && (
        <p className="mt-3 text-xs text-stone-500">
          AI assist is switched off. Every field below can be filled in by hand as usual.
        </p>
      )}

      {lastResult?.mode === 'failed' && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-xs text-red-800">
            {lastResult.error || 'Extraction failed.'}
          </p>
          <p className="mt-0.5 text-xs text-red-700">
            AI assist is on, but this attempt did not complete. Fill the fields below by
            hand, or try another document.
          </p>
        </div>
      )}
    </div>
  );
};

export default SmartFillPanel;
